/**
 * Operator groups for value-kind FieldTypes.
 *
 * Subtypes that inherit from their parent (markdown, integer, currency, percent,
 * filter, visibility) are absent here and resolved via the chain walk in
 * `../operators`.
 *
 * Lives outside the FieldType files so `../operators` can import it as pure data:
 * the per-type FieldType files carry no operator definitions.
 */

import { isNotOneOf, isOneOf } from "./array.js"
import { after, before, onOrAfter, onOrBefore } from "./date.js"
import { equals, equalsCI, notEquals, notEqualsCI } from "./equality.js"
import type { OperatorGroup } from "./index.js"
import { greaterThan, greaterThanOrEqual, lessThan, lessThanOrEqual } from "./numeric.js"
import { isEmpty, isFalse, isNotEmpty, isTrue } from "./presence.js"
import { between, notBetween } from "./range.js"
import { contains, endsWith, notContains, startsWith } from "./text.js"

const presence: OperatorGroup = { label: "Presence", operators: [isEmpty, isNotEmpty] }

export const operatorGroups: Record<string, OperatorGroup[]> = {
  string: [
    { label: "Match", operators: [equals, notEquals, equalsCI, notEqualsCI] },
    { label: "Text", operators: [contains, notContains, startsWith, endsWith] },
    { label: "Set", operators: [isOneOf, isNotOneOf] },
    presence,
  ],
  number: [
    { label: "Match", operators: [equals, notEquals] },
    {
      label: "Compare",
      operators: [greaterThan, greaterThanOrEqual, lessThan, lessThanOrEqual],
    },
    { label: "Range", operators: [between, notBetween] },
    { label: "Set", operators: [isOneOf, isNotOneOf] },
    presence,
  ],
  boolean: [{ label: "Value", operators: [isTrue, isFalse] }],
  timestamp: [
    { label: "Match", operators: [equals, notEquals] },
    { label: "Compare", operators: [before, after, onOrBefore, onOrAfter] },
    { label: "Range", operators: [between, notBetween] },
    presence,
  ],
  select: [
    { label: "Match", operators: [equals, notEquals] },
    { label: "Set", operators: [isOneOf, isNotOneOf] },
    presence,
  ],
  object: [presence],
}
